import React, { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { maintenanceService } from '@/services/maintenanceService'
import './App.css'
import Header from './components/layout/Header'
import Footer from './components/layout/Footer'
import ErrorBoundary from './components/common/ErrorBoundary'
import MaintenanceGate from './components/common/MaintenanceGate'
import MobileActionBar from './components/layout/MobileActionBar'
import MutationLockOverlay from './components/common/MutationLockOverlay'
import UpdateAvailableToast from './components/common/UpdateAvailableToast'
import { authRoutes, protectedRoutes, errorRoutes } from './config/routes'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { AUTH_MAINTENANCE_ENABLED } from './config/maintenance'
import { useAuth } from './hooks/useAuth'

const ZOOM_CHAT_SCRIPT_ID = 'zoom-live-chat-sdk'

function ProtectedRoute({ element }: { element: React.ReactElement }): React.JSX.Element {
  const { isAuthenticated } = useAuth()
  const location = useLocation()

  if (isAuthenticated && !AUTH_MAINTENANCE_ENABLED) {
    return element
  }

  // Always carry the intended path so the customer lands back where they were
  // aiming after signing in, e.g. a one-off product link from an email (DF-230).
  // Previously the return path was dropped for clean URLs with no query string.
  const redirectTo = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`

  return <Navigate to={redirectTo} replace />
}

function AppRoutes(): React.JSX.Element {
  const { isAuthenticated } = useAuth()

  // Admin switch for the in-portal live chat (hotfix/chat). null until the check
  // resolves; a failed check falls back to on, so chat is never hidden wrongly.
  const [chatEnabled, setChatEnabled] = useState<boolean | null>(null)
  useEffect(() => {
    maintenanceService.getStatus()
      .then(status => setChatEnabled(status.chatEnabled))
      .catch(() => setChatEnabled(true))
  }, [])

  useEffect(() => {
    // Only load the Zoom SDK once chat is a confirmed yes. Loading it can't be
    // undone: the SDK mounts its own widget, which removing the <script> tag
    // does not take down. So we wait rather than inject on the default and try
    // to claw it back.
    if (!isAuthenticated || chatEnabled !== true) {
      document.getElementById(ZOOM_CHAT_SCRIPT_ID)?.remove()
      return
    }

    if (document.getElementById(ZOOM_CHAT_SCRIPT_ID)) {
      return
    }

    const script = document.createElement('script')
    script.id = ZOOM_CHAT_SCRIPT_ID
    script.type = 'module'
    script.src = 'https://us01ccistatic.zoom.us/us01cci/web-sdk/zcc-sdk.js'
    script.setAttribute('data-apikey', '85JF-fXEQWegzArf3L5v7g')
    script.setAttribute('data-env', 'us01')
    document.head.appendChild(script)

    return () => {
      script.remove()
    }
  }, [isAuthenticated, chatEnabled])

  // The /login route is a self-contained experience: full-bleed
  // background, centred popup, its own minimal footer beneath the card.
  // The global Header (account nav) and Footer (copyright + legal
  // links) compete with that and would force the popup to scroll.
  // Detect the path here and skip the chrome on the auth-shell pages
  // — login and forgot-password share the same two-column popup
  // layout, so neither should render the global chrome on top.
  const location = useLocation()

  // Inside the router, so it can see each navigation.
  useDocumentTitle()

  const isBareLayout =
    location.pathname === '/login' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password' ||
    location.pathname === '/start' ||
    // DF-345: the Dynamics iframe view renders on its own, no app chrome.
    location.pathname === '/crm/deliveries'

  return (
    <>
      {!isBareLayout && <Header />}
      <main className={isBareLayout ? 'app-main app-main--bare' : 'app-main'}>
        <Routes>
          {authRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={route.element}
            />
          ))}

          {protectedRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={<ProtectedRoute element={route.element as React.ReactElement} />}
            />
          ))}

          {errorRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={route.element}
            />
          ))}
        </Routes>
      </main>
      {!isBareLayout && <Footer />}
      {isAuthenticated && !isBareLayout && <MobileActionBar />}
    </>
  )
}

function App(): React.JSX.Element {
  return (
    <ErrorBoundary>
      <MaintenanceGate>
        <div className="app">
          <AppRoutes />
          <MutationLockOverlay />
          <UpdateAvailableToast />
        </div>
      </MaintenanceGate>
    </ErrorBoundary>
  )
}

export default App
